import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import arrow from "../imgs/arrow.svg"; // your arrow image path
import logo from "../svg/logo.svg"; // your close icon image path
import heroText from "../svg/hero-text.svg";
import vid from "../vid/stunnerave.mp4";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export default function Header() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const heroTextRef = useRef(null);
  const location = useLocation();

  const toggleMenu = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    if (location.pathname === "/" && heroTextRef.current) {
      const heroElement = heroTextRef.current;

      // Wait for ScrollTrigger to be ready
      ScrollTrigger.refresh();

      // Create ScrollTrigger that pins and animates hero text
      const scrollTrigger = ScrollTrigger.create({
        trigger: heroElement,
        start: "top top",
        end: "+=1000", // Increased from 600 for slower hero text animation
        pin: true,
        pinSpacing: true,
        scrub: 2, // Increased from 1 for smoother, slower animation
        onUpdate: (self) => {
          const progress = self.progress;
          // Move hero text up smoothly
          gsap.to(heroElement, {
            y: -progress * window.innerHeight * 0.8,
            duration: 0.1,
            ease: "power2.out",
          });
        },
      });

      return () => {
        scrollTrigger.kill();
      };
    }
  }, [location.pathname]);

  // Conditionally apply bg-orange-500 when the current route is /tickets
  const ticketLinkClass =
    location.pathname === "/tickets"
      ? "border border-border-subtle px-1 items-center py-1 bg-orange-500 hover:bg-orange-600"
      : "border border-border-subtle px-1 items-center py-1 hover:bg-orange-500";

  return (
    <div className="relative">
      {/* Fixed Navbar - Logo and Menu */}
      <nav className="fixed top-0 left-0 right-0 flex items-center justify-between mx-5 py-3 z-50">
        {/* Logo */}
        <div className="text-left">
          <Link to="/" className="font-space_age font-bold text-xl md:text-2xl">
            <img src={logo} alt="stunner rave logo " className="w-[50px]" />
          </Link>
        </div>

        {/* Menu Button */}
        <div>
          <button
            onClick={toggleMenu}
            className="bg-black text-white px-4 py-2 rounded-md font-Neuehaasdisplay text-sm font-light hover:bg-gray-800 transition-colors"
          >
            {isSidebarOpen ? "Close" : "[MENU]"}
          </button>
        </div>
      </nav>

      {/* Hero Text Section - Moves up and hides on scroll with clipping mask */}
      {location.pathname === "/" && (
        <div
          ref={heroTextRef}
          className="h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px] xl:h-[800px] pt-16 sm:pt-20 pb-8 sm:pb-12 md:pb-[50px] flex items-center justify-center pointer-events-none z-40 bg-white/50"
          style={{
            position: "relative",
            width: "100%",
            maxWidth: "100%",
            marginTop: 0,
            marginLeft: 0,
            marginRight: 0,
          }}
        >
          {/* Container with SVG as clipping mask - video shows through text shape */}
          <div
            className="relative w-full px-4 sm:px-6 md:px-8 lg:px-12 flex items-center justify-center overflow-hidden"
            style={{
              maskImage: `url(${heroText})`,
              WebkitMaskImage: `url(${heroText})`,
              maskSize: "contain",
              maskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskSize: "contain",
              WebkitMaskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              minHeight: "100%",
              width: "100%",
            }}
          >
            {/* Video positioned to match the fixed video behind - same source, synced */}
            <video
              autoPlay
              loop
              muted
              playsInline
              src={vid}
              className="absolute inset-0 w-full h-full object-cover masked-video"
              style={{
                minHeight: "100%",
                objectFit: "cover",
                // Position to match the fixed video
                top: 0,
                left: 0,
              }}
              onLoadedMetadata={(e) => {
                // Sync with the fixed video if it exists
                const fixedVideo = document.querySelector(".fixed-video");
                if (fixedVideo) {
                  e.target.currentTime = fixedVideo.currentTime;
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Sidebar Menu - Visible on all screens */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-black text-white transform ${
          isSidebarOpen ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out z-50`}
      >
        {/* Close Button */}
        <div className="flex justify-end p-5">
          <button
            onClick={toggleMenu}
            className="text-white hover:text-gray-300 transition-colors flex items-center gap-2"
            aria-label="Close menu"
          >
            <span className="font-Neuehaasdisplay text-sm tracking-widest">
              CLOSE
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        {/* Sidebar Content */}
        <div className="px-8 pb-8">
          <ul className="space-y-6 font-Neuehaasdisplay font-light">
            <li className="border-b border-border-subtle py-3">
              <Link
                to="/merch"
                onClick={toggleMenu}
                className="text-lg leading-normal tracking-widest hover:font-bold transition-all"
              >
                MERCH
              </Link>
            </li>
            <li className="border-b border-border-subtle py-3">
              <Link
                to="/about"
                onClick={toggleMenu}
                className="text-lg leading-normal tracking-widest hover:font-bold transition-all"
              >
                ABOUT US
              </Link>
            </li>
            <li className="border-b border-border-subtle py-3">
              <Link
                to="/contact"
                onClick={toggleMenu}
                className="text-lg leading-normal tracking-widest hover:font-bold transition-all"
              >
                CONTACT US
              </Link>
            </li>
            <li className="border-b border-border-subtle py-3">
              <Link
                to="/tickets"
                onClick={toggleMenu}
                className="text-lg leading-normal tracking-widest hover:font-bold transition-all"
              >
                TICKETS
              </Link>
            </li>
            <li className={`py-3 ${ticketLinkClass}`}>
              <img
                src={arrow}
                alt="arrowsvg"
                className="w-5 inline-block mr-2"
              />
              <Link
                to="/vendorpayment"
                className="text-lg leading-normal tracking-widest"
                onClick={toggleMenu}
              >
                BECOME A VENDOR
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Overlay when sidebar is open */}
      {isSidebarOpen && (
        <div
          onClick={toggleMenu}
          className="fixed inset-0 bg-black opacity-50 z-40"
        ></div>
      )}
    </div>
  );
}
